import { createFileRoute } from '@tanstack/react-router'
import { type ReactElement } from 'react'

import { assertDefinedAndNotNull } from '@orus.eu/error'
import { isSuccess } from '@orus.eu/result'

import { sessionManager } from '#/lib/session.ts'
import { AddPaymentMethodPageSkeleton } from '#/modules/payment/front-office/add-payment-method-page/add-payment-method-page-skeleton.tsx'
import { AddPaymentMethodPage } from '#/modules/payment/front-office/add-payment-method-page/add-payment-method-page.tsx'
import { requirePermissions } from '#/shared/routing/require-permissions.ts'

function AddPaymentMethodRoute(): ReactElement {
  const { subscriptionId } = Route.useParams()
  return <AddPaymentMethodPage subscriptionId={subscriptionId} />
}

export const Route = createFileRoute('/(customer)/_shell/contracts-v2/$subscriptionId/payment-methods/new/')({
  beforeLoad: ({ context }) => requirePermissions(context, ['paymentAccount.update']),
  loader: async ({ context, params }) => {
    const sessionResult = await sessionManager.getSessionResult()
    const userId = isSuccess(sessionResult)
      ? assertDefinedAndNotNull(sessionResult.output.user, 'Payment methods require a logged-in user', {}).id
      : undefined

    return Promise.all([
      // `includeTerminatedBeforeOneMonth` must match the screen's own query: a different input is a different
      // cache key, and the loader would warm an entry the component never reads.
      context.queryClient.ensureQueryData(
        context.trpc.contracts.getMyContract.queryOptions({
          subscriptionId: params.subscriptionId,
          includeTerminatedBeforeOneMonth: true,
        }),
      ),
      context.queryClient.ensureQueryData(
        context.trpc.paymentMethod.getAllowedPaymentMethodsBySubscriptionId.queryOptions({
          subscriptionId: params.subscriptionId,
        }),
      ),
      context.queryClient.ensureQueryData(context.trpc.users.loadMyInformation.queryOptions()),
      context.queryClient.ensureQueryData(context.trpc.contracts.getLastCustomerAddress.queryOptions()),
      ...(userId
        ? [
            context.queryClient.ensureQueryData(
              context.trpc.paymentMethod.getDebitablePaymentMethods.queryOptions({ userId }),
            ),
          ]
        : []),
    ])
  },
  pendingComponent: AddPaymentMethodPageSkeleton,
  pendingMs: 150,
  component: AddPaymentMethodRoute,
})
